import { FC } from 'react';
import { Control, Controller } from 'react-hook-form';

import SelectDropdown from 'components/SelectDropdown/SelectDropdown';
import { PaymentHoldApplyTo } from 'interfaces/stripe';

export interface PaymentSettingsFormValues {
  isStripeCheckoutEnabled: boolean;
  paymentHoldApplyTo: PaymentHoldApplyTo;
  bookingDepositAmount: number;
  paymentHoldWithinDays: number | null;
}

interface PaymentSettingsFormProps {
  control: Control<PaymentSettingsFormValues>;
}

const APPLY_HOLD_TO_OPTIONS = [
  { value: PaymentHoldApplyTo.ALL_PATIENTS, label: 'All patients' },
  { value: PaymentHoldApplyTo.NEW_PATIENTS, label: 'New patients' },
];

const PAYMENT_AUTHORIZATION_AMOUNTS = [25, 50, 75, 100, 125, 150, 175, 200];

const PAYMENT_HOLD_WITHIN_DAYS_ANY = 'any';

const PAYMENT_HOLD_WITHIN_DAYS_OPTIONS = [
  { value: PAYMENT_HOLD_WITHIN_DAYS_ANY, label: 'Any time' },
  { value: 1, label: '1 day' },
  { value: 2, label: '2 days' },
  { value: 3, label: '3 days' },
  { value: 4, label: '4 days' },
];

const PaymentSettingsForm: FC<PaymentSettingsFormProps> = ({ control }) => {
  return (
    <div className="flex flex-col gap-y-3.2">
      <section className="flex flex-col gap-y-1.6">
        <p className="font-bold text-16 leading-[2.4rem] text-darkest-grey">
          Apply Hold To
        </p>
        <Controller
          name="paymentHoldApplyTo"
          control={control}
          render={({ field: { value, onChange } }) => (
            <SelectDropdown
              options={APPLY_HOLD_TO_OPTIONS}
              value={value}
              onChange={(newValue) => onChange(newValue as PaymentHoldApplyTo)}
            />
          )}
        />
      </section>

      <section className="flex flex-col gap-y-1.6">
        <p className="font-bold text-16 leading-[2.4rem] text-darkest-grey">
          Payment Authorization Amount
        </p>
        <Controller
          name="bookingDepositAmount"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="flex flex-wrap gap-1.2">
              {PAYMENT_AUTHORIZATION_AMOUNTS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => onChange(amount)}
                  className={`rounded-full border px-2.4 py-1 text-16 leading-[2.4rem] ${
                    value === amount
                      ? 'border-magenta bg-magenta-lightest text-magenta font-bold'
                      : 'border-grey bg-white text-darkest-grey'
                  }`}
                >
                  ${amount}
                </button>
              ))}
            </div>
          )}
        />
      </section>

      <section className="flex flex-col gap-y-1.6">
        <p className="font-bold text-16 leading-[2.4rem] text-darkest-grey">
          Only require hold if booking is within X days
        </p>
        <Controller
          name="paymentHoldWithinDays"
          control={control}
          render={({ field: { value, onChange } }) => (
            <SelectDropdown
              options={PAYMENT_HOLD_WITHIN_DAYS_OPTIONS}
              value={value ?? PAYMENT_HOLD_WITHIN_DAYS_ANY}
              onChange={(newValue) =>
                onChange(
                  newValue === PAYMENT_HOLD_WITHIN_DAYS_ANY
                    ? null
                    : Number(newValue)
                )
              }
            />
          )}
        />
      </section>
    </div>
  );
};

export default PaymentSettingsForm;
